// src/pages/auth/ForgotPasswordPage.jsx
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import Button from '../../components/common/Button';
import InputField from '../../components/common/InputField';
import { forgotPassword } from '../../services/authService';
import { 
  FaEnvelope, 
  FaPlane, 
  FaArrowLeft,
  FaCheckCircle,
  FaGlobe,
  FaUsers,
  FaHeart
} from 'react-icons/fa';
import { MdOutlineMarkEmailRead } from 'react-icons/md';

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [sentEmail, setSentEmail] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch
  } = useForm({
    mode: 'onChange'
  });

  const email = watch('email', '');

// In ForgotPasswordPage.jsx, update the onSubmit function:

const onSubmit = async (data) => {
  setIsLoading(true);
  try {
    const response = await forgotPassword(data.email);
    
    if (response.success) {
      setSentEmail(data.email);
      setEmailSent(true);
      
      // Store user data for next steps
      if (response.data?.userId) {
        localStorage.setItem('resetUserId', response.data.userId);
        localStorage.setItem('resetEmail', data.email);
      }
      
      toast.success(
        <div className="flex items-center">
          <MdOutlineMarkEmailRead className="text-2xl mr-2" />
          <div>
            <p className="font-semibold">Reset OTP sent!</p>
            <p className="text-sm">Check your email for the 6-digit OTP.</p>
          </div>
        </div>
      );
      
      // Navigate to reset password page
      setTimeout(() => {
        navigate('/reset-password');
      }, 1500);
    } else {
      toast.error(response.message || 'Failed to send reset OTP');
    }
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.message ||
                        'Failed to send reset OTP. Please try again.';
    toast.error(errorMessage);
  } finally {
    setIsLoading(false);
  }
};

  return (
    <div className="min-h-screen w-full bg-gradient-to-b from-gray-50 to-gray-100 m-0 p-0 relative">
      {/* Fixed Background */}
      <div className="fixed inset-0 bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-700 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <svg className="absolute bottom-0 w-full h-48" preserveAspectRatio="none" viewBox="0 0 1440 120">
            <path fill="white" d="M0,32L48,37.3C96,43,192,53,288,58.7C384,64,480,64,576,58.7C672,53,768,43,864,48C960,53,1056,75,1152,80C1248,85,1344,75,1392,69.3L1440,64L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z"></path>
          </svg>
        </div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 transform rotate-12">
            <FaPlane className="text-white text-8xl" />
          </div>
          <div className="absolute bottom-10 right-10 transform -rotate-45">
            <FaPlane className="text-white text-8xl" />
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="relative z-10 min-h-screen w-full overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen py-8 md:py-12">
          <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              {/* Left side - Welcome text */}
              <div className="text-center lg:text-left">
                <div className="inline-block bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
                  🔐 Password Recovery
                </div>
                <h1 className="text-4xl lg:text-5xl font-bold mb-4 leading-tight text-white">
                  Forgot Your 
                  <span className="block text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-pink-300">
                    Password?
                  </span>
                </h1>
                <p className="text-lg text-blue-100 max-w-xl mx-auto lg:mx-0 mb-6">
                  Don't worry! Enter your email and we'll send you instructions to reset your password securely.
                </p>
                
                {/* Quick Stats */}
                <div className="flex flex-wrap gap-6 justify-center lg:justify-start">
                  <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                    <FaGlobe className="text-yellow-300" />
                    <span className="text-white">24/7 Support</span>
                  </div>
                  <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                    <FaUsers className="text-yellow-300" />
                    <span className="text-white">Secure Recovery</span>
                  </div>
                  <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                    <FaHeart className="text-yellow-300" />
                    <span className="text-white">Instant Help</span>
                  </div>
                </div>
              </div>

              {/* Right side - Forgot Password Form Card */}
              <div className="w-full max-w-md mx-auto lg:mx-0 lg:ml-auto">
                <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl p-6 transform transition-all duration-500 hover:scale-105">
                  {/* Back to Login Link */}
                  <Link 
                    to="/login" 
                    className="inline-flex items-center text-sm text-gray-600 hover:text-blue-600 mb-4 transition-colors"
                  >
                    <FaArrowLeft className="mr-2 text-xs" /> Back to Login
                  </Link>

                  <div className="text-center mb-6">
                    <div className="inline-block p-3 bg-blue-100 rounded-full mb-3">
                      <FaEnvelope className="text-blue-600 text-xl" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-800">Reset Password</h2>
                    <p className="text-gray-500 text-sm mt-1">
                      {!emailSent 
                        ? "Enter your email to receive reset instructions" 
                        : "Check your email for the reset link"}
                    </p>
                  </div>

                  {!emailSent ? (
                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                      {/* Email Field */}
                      <div>
                        <InputField
                          label="Email Address"
                          type="email"
                          placeholder="Enter your registered email"
                          icon={<FaEnvelope className="text-gray-400" />}
                          error={errors.email}
                          {...register('email', {
                            required: 'Email is required',
                            pattern: {
                              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                              message: 'Invalid email address'
                            }
                          })}
                        />
                        {!errors.email && email && (
                          <p className="mt-1 text-xs text-green-600 flex items-center">
                            <FaCheckCircle className="mr-1" /> Valid email format
                          </p>
                        )}
                      </div>

                      {/* Submit Button */}
                      <Button
                        type="submit"
                        className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-2 rounded-lg transition-all transform hover:scale-[1.02] disabled:opacity-50"
                        isLoading={isLoading}
                        disabled={isLoading || Object.keys(errors).length > 0}
                      >
                        Send Reset Instructions
                      </Button>

                      {/* Additional Help */}
                      <p className="text-xs text-center text-gray-500 mt-4">
                        We'll send a password reset link to your email. 
                        The link will expire in 1 hour for security.
                      </p>
                    </form>
                  ) : (
                    /* Success State */
                    <motion.div 
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-center space-y-5"
                    >
                      <div className="flex justify-center">
                        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
                          <MdOutlineMarkEmailRead className="text-green-600 text-4xl" />
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <h3 className="text-lg font-semibold text-gray-800">Check Your Email</h3>
                        <p className="text-sm text-gray-600">
                          We've sent reset instructions to:<br />
                          <span className="font-medium text-blue-600">{sentEmail}</span>
                        </p>
                      </div>

                      <div className="bg-blue-50 rounded-lg p-4 text-sm text-blue-700">
                        <p>📧 Didn't receive the email? Check your spam folder or</p>
                        <button 
                          onClick={() => setEmailSent(false)}
                          className="text-blue-600 font-medium hover:underline mt-1"
                        >
                          try another email address
                        </button>
                      </div>

                      <div className="pt-4">
                        <Link 
                          to="/login" 
                          className="text-blue-600 hover:text-blue-700 font-medium inline-flex items-center"
                        >
                          <FaArrowLeft className="mr-2 text-xs" /> Back to Login
                        </Link>
                      </div>
                    </motion.div>
                  )}

                  {/* Trust Badges */}
                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <div className="flex justify-center gap-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <FaCheckCircle className="text-green-500" /> Secure Process
                      </span>
                      <span className="flex items-center gap-1">
                        <FaUsers className="text-blue-500" /> 24/7 Support
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-2px); }
          20%, 40%, 60%, 80% { transform: translateX(2px); }
        }
        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }
      `}</style>
    </div>
  );
};

export default ForgotPasswordPage;